// Gate card — the new map "tile". Replaces abstract gate squares with a
// phase-aware card showing what's happening at that gate right now.
//
// Layout (matches the dispatcher's sketch):
//   ╭─ C25 ─╮               ← gate label (small tab, sticky chrome)
//   ┌────────┐
//   │WN 1842 │  ← header strip (flight + carrier + route times)
//   ├────────┤
//   │ phase  │  ← body: phase-specific content
//   │ stats  │     - download: timer + scan progress
//   └────────┘     - upload:   timer + onload progress
//                  - inbound:  ETA countdown
//                  - push:     pushback timer
//                  - empty:    next scheduled inbound
//
// Border color encodes status:
//   gray   = empty / quiet
//   blue   = inbound (taxiing in)
//   cyan   = active work (download / upload)
//   gold   = push
//   red    = any open issue (overrides everything else, pulses)

import React from 'react';
import { Box, Paper, Typography, Tooltip } from '@mui/material';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import { useIntel } from './IntelligenceContext';
import { useLiveGateDetail } from './useLiveDetail';
import { alertLevelFor, ALERT_COLORS } from './mockGateDetails';
import { C } from '../styles';

// Card geometry — sized to comfortably hold the canonical app
// typography (0.7-0.85rem) without crowding.
export const CARD_W = 224;
export const CARD_H = 150;    // body + header
export const LABEL_H = 20;    // gate label "C25" tab above

// Phases are *informational text only* — they tell the dispatcher what's
// happening at the gate, but they don't paint color. Color on a gate
// card means "this needs your attention," which is driven by issue
// severity (see alertLevelFor), not by phase. Quiet gates stay quiet.
const PHASE_META = {
  empty:    { label: 'EMPTY' },
  inbound:  { label: 'INBOUND' },
  arriving: { label: 'AT GATE' },
  download: { label: 'DOWNLOAD' },
  upload:   { label: 'UPLOAD' },
  push:     { label: 'PUSH' },
};

// Neutral chrome for the quiet baseline. Alert colors overlay this only
// when alertLevelFor(detail) is non-null.
const QUIET_BORDER = 'rgba(255, 255, 255, 0.10)';
const QUIET_TEXT   = 'rgba(255, 255, 255, 0.55)';

function GateCard({ entity, detail: staticDetail }) {
  const intel = useIntel();
  const { setSelected, hasAnnotations, demoTick, selected } = intel;

  const detail = useLiveGateDetail(staticDetail);
  const phaseMeta = PHASE_META[detail.phase] || PHASE_META.empty;
  const isSelected = selected?.kind === 'gate' && selected.id === detail.gateId;
  const noted = hasAnnotations(entity);

  const hasIssue = detail.issues?.length > 0;
  const alertLevel = alertLevelFor(detail);   // 'red' | 'gold' | 'blue' | null
  const alertColor = alertLevel ? ALERT_COLORS[alertLevel] : null;
  const hottest = detail.connections.filter(c => c.isHot).sort((a, b) => a.connectionMin - b.connectionMin)[0];

  // Border cascade: selection > alert > quiet baseline. Quiet gates
  // get a barely-there gray so the dispatcher's eye skips them.
  const borderColor = isSelected ? C.accent
                    : alertColor ? alertColor
                    : QUIET_BORDER;
  // Pulse only on RED — that's the "act now" signal.
  const pulses = alertLevel === 'red';

  return (
    <Box
      data-intel-overlay
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => { e.stopPropagation(); setSelected({ kind: 'gate', id: detail.gateId }); }}
      sx={{
        width: CARD_W,
        cursor: 'pointer',
        display: 'flex', flexDirection: 'column',
        userSelect: 'none',
        ...(pulses ? {
          '@keyframes cardPulse': {
            '0%, 100%': { boxShadow: `0 0 0 0 ${borderColor}80, 0 0 12px ${borderColor}55` },
            '50%':      { boxShadow: `0 0 0 3px ${borderColor}40, 0 0 18px ${borderColor}77` },
          },
        } : {}),
      }}
    >
      {/* Gate label — tab "C25" */}
      <Box sx={{
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        height: LABEL_H, gap: 0.5,
      }}>
        <Typography sx={{
          fontSize: '0.85rem', color: isSelected ? C.accent : C.textPrimary,
          fontWeight: 800, letterSpacing: 0.6,
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        }}>
          {detail.gateId}
        </Typography>
        {noted && (
          <StickyNote2Icon sx={{ fontSize: 12, color: C.accent }} />
        )}
        {hasIssue && (
          <Box sx={{
            ml: 0.2,
            minWidth: 16, height: 16,
            px: '5px',
            borderRadius: '8px',
            backgroundColor: alertColor,
            // Red and blue use white text for legibility; gold uses
            // black because the gold itself is bright.
            color: alertLevel === 'gold' ? '#000' : '#fff',
            fontSize: '0.65rem', fontWeight: 800,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            lineHeight: 1,
          }}>
            {detail.issues.length}
          </Box>
        )}
      </Box>

      {/* Card body — canonical inner sub-card pattern (matches the
          .breakdown-subcard treatment from FlightDataPage.css) */}
      <Paper
        elevation={0}
        sx={{
          height: CARD_H,
          backgroundColor: 'rgba(17, 25, 36, 0.35)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          border: `1px solid ${borderColor}`,
          borderRadius: 1.5,
          overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
          animation: pulses ? 'cardPulse 1.6s ease-in-out infinite' : 'none',
          boxShadow: isSelected
            ? `0 0 0 1px ${C.accent}, inset 0 0 12px rgba(17, 25, 36, 0.15), 0 16px 28px rgba(0, 0, 0, 0.30)`
            : 'inset 0 0 12px rgba(17, 25, 36, 0.15), 0 16px 28px rgba(0, 0, 0, 0.25)',
          transition: 'background 200ms ease, transform 200ms ease, box-shadow 200ms ease',
          '&:hover': {
            backgroundColor: 'rgba(17, 25, 36, 0.5)',
            transform: 'translateY(-2px)',
            boxShadow: 'inset 0 0 18px rgba(17, 25, 36, 0.25), 0 22px 36px rgba(0, 0, 0, 0.30)',
          },
        }}
      >
        {/* Header strip — flight + route. Only tinted when there's an
            alert; quiet gates have a transparent header so they recede. */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 0.6,
          px: 1.25, py: 0.6,
          backgroundColor: alertColor ? `${alertColor}1F` : 'transparent',
          borderBottom: alertColor
            ? `1px solid ${alertColor}55`
            : `1px solid ${QUIET_BORDER}`,
          flexShrink: 0,
        }}>
          {detail.phase === 'empty' ? (
            <Typography sx={{
              fontSize: '0.72rem', color: C.textMuted, fontWeight: 800,
              letterSpacing: 0.7, textTransform: 'uppercase',
            }}>
              Empty
            </Typography>
          ) : (
            <>
              <Typography noWrap sx={{
                fontSize: '0.92rem', color: C.textPrimary, fontWeight: 700,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                letterSpacing: 0.2,
              }}>
                {detail.flightNumber.replace(/^WN\s*/, 'WN ')}
              </Typography>
              {hottest && (
                <Tooltip title={`Hot bag → ${hottest.flightNumber} (${hottest.makeConfidence}% make, pushes in ${hottest.connectionMin}m)`}>
                  <WhatshotIcon sx={{
                    fontSize: 14,
                    // Red only when truly critical (<15m). Gold for at-risk
                    // (<75% confidence). Otherwise muted — the dispatcher
                    // doesn't need to glance at "fine" hot bags.
                    color: hottest.connectionMin <= 15 ? '#FF0000'
                         : hottest.makeConfidence < 75 ? '#F9B612'
                         : QUIET_TEXT,
                  }} />
                </Tooltip>
              )}
              <Box sx={{ flex: 1 }} />
              <Typography sx={{
                fontSize: '0.7rem', color: C.textSecondary, fontWeight: 700,
                fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
              }}>
                {detail.eta || '—'} → {detail.etd || '—'}
              </Typography>
            </>
          )}
        </Box>

        {/* Phase body */}
        <Box sx={{ flex: 1, px: 1.25, py: 0.75, display: 'flex', flexDirection: 'column', gap: 0.5, minHeight: 0 }}>
          <PhaseBody detail={detail} phaseMeta={phaseMeta} demoTick={demoTick} />
          {detail.phase !== 'empty' && (
            <CrewStrip detail={detail} />
          )}
        </Box>
      </Paper>
    </Box>
  );
}

// What goes inside the card body, per phase.
function PhaseBody({ detail, phaseMeta, demoTick }) {
  const elapsed = (detail.phaseStartedSecAgo ?? 0) + demoTick;
  const elapsedLabel = formatElapsed(elapsed);

  switch (detail.phase) {
    case 'inbound': {
      const minsAway = Math.max(0, (detail.nextInbound?.minutesAway ?? 8));
      return (
        <>
          <PhaseTag meta={phaseMeta} right={`ETA ${minsAway}m`} />
          <SmallLine>From {detail.origin}</SmallLine>
          <SmallLine muted>{detail.tail}</SmallLine>
        </>
      );
    }
    case 'arriving': {
      return (
        <>
          <PhaseTag meta={phaseMeta} right={elapsedLabel} />
          <SmallLine>{detail.drivers.length} crew · {detail.tug ? 'Tug ✓' : 'No tug'}</SmallLine>
          <SmallLine muted>From {detail.origin}</SmallLine>
        </>
      );
    }
    case 'download': {
      return (
        <>
          <PhaseTag meta={phaseMeta} right={elapsedLabel} />
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.4 }}>
            <Typography sx={{
              fontSize: '1.1rem', color: C.textPrimary, fontWeight: 800,
              fontVariantNumeric: 'tabular-nums', lineHeight: 1,
            }}>
              {detail.scannedBags}
            </Typography>
            <Typography sx={{
              fontSize: '0.78rem', color: C.textMuted, fontWeight: 600,
              fontVariantNumeric: 'tabular-nums', lineHeight: 1,
            }}>
              / {detail.totalBags} bags
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Typography sx={{
              fontSize: '0.85rem', color: C.textPrimary, fontWeight: 800,
              fontVariantNumeric: 'tabular-nums', lineHeight: 1,
            }}>
              {detail.bagPct}%
            </Typography>
          </Box>
          <ProgressBar pct={detail.bagPct} color={QUIET_TEXT} />
        </>
      );
    }
    case 'upload': {
      const pct = detail.uploadTotal > 0 ? Math.round((detail.uploadLoaded / detail.uploadTotal) * 100) : 0;
      return (
        <>
          <PhaseTag meta={phaseMeta} right={elapsedLabel} />
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.4 }}>
            <Typography sx={{
              fontSize: '1.1rem', color: C.textPrimary, fontWeight: 800,
              fontVariantNumeric: 'tabular-nums', lineHeight: 1,
            }}>
              {detail.uploadLoaded}
            </Typography>
            <Typography sx={{
              fontSize: '0.78rem', color: C.textMuted, fontWeight: 600,
              fontVariantNumeric: 'tabular-nums', lineHeight: 1,
            }}>
              / {detail.uploadTotal} loaded
            </Typography>
            <Box sx={{ flex: 1 }} />
            {detail.hotPending > 0 && (
              <Typography sx={{
                fontSize: '0.7rem', color: '#FF0000', fontWeight: 800,
                letterSpacing: 0.4, textTransform: 'uppercase',
              }}>
                {detail.hotPending} hot
              </Typography>
            )}
          </Box>
          <ProgressBar pct={pct} color={QUIET_TEXT} />
        </>
      );
    }
    case 'push': {
      return (
        <>
          <PhaseTag meta={phaseMeta} right={elapsedLabel} />
          <SmallLine>Pushback in progress</SmallLine>
          <SmallLine muted>Doors closed · cleared</SmallLine>
        </>
      );
    }
    case 'empty':
    default: {
      const next = detail.nextInbound;
      if (next) {
        // Lead with the actual ETA time (e.g., "13:45") on the right
        // and the minutes-away countdown next to the flight number.
        // Phase chip describes whether it's in_air / on_ground / taxiing.
        const phaseLabel = next.phase === 'taxiing'   ? 'Taxiing in'
                         : next.phase === 'on_ground' ? 'On ground'
                         : 'In air';
        return (
          <>
            <PhaseTag meta={phaseMeta} right={next.eta || `${next.minutesAway}m`} />
            <SmallLine>
              {next.flightNumber.replace(/^WN\s*/, 'WN ')}
              {' · '}
              <Box component="span" sx={{ color: C.textMuted }}>
                from {next.origin}
              </Box>
            </SmallLine>
            <SmallLine muted>{phaseLabel} · in {next.minutesAway}m</SmallLine>
          </>
        );
      }
      return (
        <SmallLine muted>Nothing else today</SmallLine>
      );
    }
  }
}

function PhaseTag({ meta, right }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography sx={{
        fontSize: '0.7rem', color: QUIET_TEXT, fontWeight: 800,
        letterSpacing: 0.7, textTransform: 'uppercase',
      }}>
        {meta.label}
      </Typography>
      <Box sx={{ flex: 1 }} />
      {right && (
        <Typography sx={{
          fontSize: '0.72rem', color: C.textPrimary, fontWeight: 800,
          fontVariantNumeric: 'tabular-nums',
        }}>
          {right}
        </Typography>
      )}
    </Box>
  );
}

function SmallLine({ children, muted }) {
  return (
    <Typography noWrap sx={{
      fontSize: '0.78rem', color: muted ? C.textMuted : C.textSecondary,
      lineHeight: 1.3,
    }}>
      {children}
    </Typography>
  );
}

// Crew + tug strip — always present at the bottom of every active card.
function CrewStrip({ detail }) {
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 0.5, mt: 'auto',
      pt: 0.5, borderTop: `1px solid ${C.border}`,
    }}>
      {detail.drivers.map(d => {
        const color = d.status === 'late'    ? '#FF0000'
                    : d.status === 'pending' ? '#F9B612'
                    : d.status === 'arrived' ? '#00BCD4'
                    : d.status === 'in_run'  ? '#2196F3'
                    : '#4CAF50';
        return (
          <Tooltip key={d.id} title={`${d.role} · ${d.name} · ${d.status}`}>
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 0.4,
              px: 0.6, py: 0.2,
              borderRadius: 0.5,
              backgroundColor: `${color}22`,
              border: `1px solid ${color}55`,
            }}>
              <Typography sx={{
                fontSize: '0.65rem', color, fontWeight: 800, letterSpacing: 0.4,
              }}>
                {d.role}
              </Typography>
              <Box sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: color }} />
            </Box>
          </Tooltip>
        );
      })}
      {detail.drivers.length < 2 && (
        <Tooltip title="Crew incomplete">
          <Box sx={{
            px: 0.6, py: 0.2, borderRadius: 0.5,
            backgroundColor: 'rgba(255, 0, 0, 0.18)',
            border: '1px solid rgba(255, 0, 0, 0.5)',
            fontSize: '0.65rem', color: '#FF0000', fontWeight: 800, letterSpacing: 0.4,
          }}>
            ?
          </Box>
        </Tooltip>
      )}
      <Box sx={{ flex: 1 }} />
      {detail.tug ? (
        <Tooltip title={`Tug ${detail.tug}`}>
          <Typography sx={{
            fontSize: '0.7rem', color: C.textMuted, fontWeight: 700,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          }}>
            {detail.tug}
          </Typography>
        </Tooltip>
      ) : (
        <Tooltip title="No tug assigned">
          <Box sx={{
            px: 0.6, py: 0.2, borderRadius: 0.5,
            backgroundColor: 'rgba(249, 182, 18, 0.18)',
            border: '1px solid rgba(249, 182, 18, 0.5)',
            fontSize: '0.6rem', color: '#F9B612', fontWeight: 800, letterSpacing: 0.5,
            textTransform: 'uppercase',
          }}>
            No Tug
          </Box>
        </Tooltip>
      )}
    </Box>
  );
}

function ProgressBar({ pct, color }) {
  return (
    <Box sx={{
      mt: 0.4, height: 3, borderRadius: 1.5,
      backgroundColor: 'rgba(255,255,255,0.06)', overflow: 'hidden',
    }}>
      <Box sx={{
        height: '100%', width: `${Math.max(0, Math.min(100, pct))}%`,
        backgroundColor: color,
        transition: 'width 200ms',
      }} />
    </Box>
  );
}

function formatElapsed(sec) {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export default GateCard;
